import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { Box, Popover, Button, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isBefore, subDays, differenceInDays, isSameDay } from 'date-fns';

import { StaticDatePicker } from '@mui/x-date-pickers';
import { DateField } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';

const CustomDatePicker = forwardRef(({ anchor, onClose, onConfirm }, ref) => {
  const { t } = useTranslation();
  const [error, setError] = useState(false);
  const [period, setPeriod] = useState({
    from: null,
    to: null,
  });
  const [periodError, setPeriodError] = useState('');
  const minDate = subDays(new Date(), 90);

  const handleChangeFrom = value => {
    setPeriodError('');

    setPeriod(prevState => ({
      ...prevState,
      from: value,
    }));
  };

  const handleChangeTo = value => {
    setPeriodError('');

    if (isBefore(value, period.from)) {
      setPeriod(prevState => ({
        from: value,
        to: value,
      }));
    } else {
      setPeriod(prevState => ({
        ...prevState,
        to: value,
      }));
    }
  };

  const handleConfirm = () => {
    if (isSameDay(period.to, period.from)) {
      setPeriodError(t('SystemConfiguration.datesCannotBeEqual'));
      return;
    }

    if (differenceInDays(period.to, period.from) > 31) {
      setPeriodError(t('SystemConfiguration.dateRangeError'));
      return;
    } else {
      onConfirm({
        from: period.from,
        to: period.to,
      });
    }
  };

  const handleClose = () => {
    setPeriodError('');
    onClose();
  };

  useImperativeHandle(ref, () => ({
    reset() {
      setPeriod({
        from: null,
        to: null,
      });
    },
  }));

  return (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <StyledRootBox>
        <Box className="pickers_box">
          <StaticDatePicker
            className="date_picker"
            slots={{ actionBar: null, toolbar: null }}
            disableFuture
            onChange={handleChangeFrom}
            value={period.from}
            minDate={minDate}
          />
          <StaticDatePicker
            className="date_picker"
            disableFuture
            slots={{ actionBar: null, toolbar: null }}
            onChange={handleChangeTo}
            value={period.to}
            minDate={minDate}
          />
        </Box>
        <Box className="error_box">
          {Boolean(periodError) && <Typography className="error_text">{periodError}</Typography>}
        </Box>
        <Box className="controls_box">
          <Box className="input_box">
            <DateField
              value={period.from}
              disableFuture
              className="date_input"
              onChange={handleChangeFrom}
              onError={error => setError(error)}
              minDate={minDate}
            />
            <Typography>{t('SystemConfiguration.to')}</Typography>
            <DateField
              value={period.to}
              disableFuture
              className="date_input"
              onChange={handleChangeTo}
              onError={error => setError(error)}
              minDate={minDate}
            />
          </Box>

          <Box className="button_box">
            <Button variant="outlined" onClick={handleClose}>
              {t('SystemConfiguration.cancel')}
            </Button>
            <Button
              disabled={!Boolean(period.to) || !Boolean(period.from) || Boolean(error)}
              variant="contained"
              onClick={handleConfirm}
            >
              {t('SystemConfiguration.apply')}
            </Button>
          </Box>
        </Box>
      </StyledRootBox>
    </Popover>
  );
});

export default CustomDatePicker;

const StyledRootBox = styled(Box)(({ theme: { palette } }) => ({
  display: 'flex',
  flexDirection: 'column',

  '.pickers_box': {
    display: 'flex',
    flexDirection: 'row',
  },

  '.error_box': {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0 12px',
    '.error_text': {
      color: palette.status.red,
    },
  },

  '.date_picker': {
    '& .MuiDialogActions-root': {
      display: 'none',
    },

    '& .MuiButtonBase-root': {
      padding: '12px',
    },

    '& .MuiPickersDay-root.Mui-disabled': {
      color: palette.whiteEmphasis.low,
    },
  },

  '.controls_box': {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '18px',
  },

  '.input_box': {
    display: 'flex',
    flexDirection: 'row',
    columnGap: '20px',
    alignItems: 'center',
    width: '50%',
    height: '36px',
    maxWidth: '400px',
  },

  '.date_input': {
    '& .MuiInputBase-input': {
      padding: '10px',
    },
  },

  '.button_box': {
    display: 'flex',
    flexDirection: 'row',
    columnGap: '20px',
  },
}));
